import { Globe, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SignalVisibilityType } from '@momentum/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select';

interface MarketplaceVisibilitySelectProps {
  visibilityType: SignalVisibilityType;
  isPublic: boolean;
  isCreator: boolean;
  onVisibilityChange: (type: SignalVisibilityType) => void;
}

export function MarketplaceVisibilitySelect({
  visibilityType,
  isPublic,
  isCreator,
  onVisibilityChange,
}: MarketplaceVisibilitySelectProps): React.ReactElement {
  const { t } = useTranslation('knowledge');

  const visibilityDisplay = (
    <div className="flex items-center gap-1.5 text-[13px] text-foreground">
      {isPublic ? (
        <>
          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
          {t('marketplacePublic')}
        </>
      ) : (
        <>
          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
          {t('marketplacePrivate')}
        </>
      )}
    </div>
  );

  if (!isCreator) {
    return (
      <div className="rounded-md px-2.5 py-2 -mx-2.5">{visibilityDisplay}</div>
    );
  }

  return (
    <Select
      value={visibilityType}
      onValueChange={(value) =>
        onVisibilityChange(value as SignalVisibilityType)
      }
    >
      <SelectTrigger className="h-auto min-h-0 w-auto rounded-md border-0 bg-transparent px-2.5 py-2 -mx-2.5 shadow-none hover:bg-black/[0.025] transition-colors [&>svg]:hidden">
        <SelectValue>{visibilityDisplay}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={SignalVisibilityType.PUBLIC}>
          <div className="flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
            {t('marketplacePublic')}
          </div>
        </SelectItem>
        <SelectItem value={SignalVisibilityType.PRIVATE}>
          <div className="flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            {t('marketplacePrivate')}
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
